
import React from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Search, RefreshCw, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { WebSearchStatus } from '@/types/webSearch';
import { StatusBadge } from './StatusBadge';

interface ProcessingStateProps {
  searchStatus: WebSearchStatus;
  progressValue: number;
  queuePosition: number | null;
  queuedAt: Date | null;
  isLoading: boolean;
  isProcessing: boolean;
  onRefresh: () => void;
  onProcessQueue: () => void;
}

const ProcessingState: React.FC<ProcessingStateProps> = ({
  searchStatus,
  progressValue,
  queuePosition,
  queuedAt,
  isLoading,
  isProcessing,
  onRefresh,
  onProcessQueue,
}) => {
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg flex items-center">
          <Search className="h-5 w-5 mr-2" />
          Company Insight
        </CardTitle>
        <CardDescription className="flex items-center">
          <span className="mr-2">
            {searchStatus === 'pending' 
              ? `Queued for web search${queuePosition !== null ? ` (#${queuePosition})` : ''}` 
              : 'Processing web search'
            }
          </span>
          <StatusBadge status={searchStatus} queuePosition={queuePosition} />
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              {searchStatus === 'pending' 
                ? 'Your lead is in the queue for web search enrichment.' 
                : 'Retrieving and processing web information about this business.'
              }
            </p>
            <Progress value={progressValue} className="h-2" />
          </div>
          
          {queuedAt && (
            <p className="text-xs text-muted-foreground">
              Queued: {format(queuedAt, 'MMM d, yyyy HH:mm')}
            </p>
          )}
          
          <div className="flex justify-center mt-4 space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={isLoading || searchStatus === 'processing'}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Status
                </>
              )}
            </Button>
            
            <Button
              variant="default"
              size="sm"
              onClick={onProcessQueue}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Process Queue
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProcessingState;
